/*
 * =============================================================================
 * FILE HEADER — packages/healing/src/fingerprint.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why              | What Changed                |
 * |---|------------------------|-------------------|------------------|-----------------------------|
 * | 1 | 2026-06-17 (Phase 2)   | Claude Sonnet 4.6 | Healing core     | Created — fingerprint build |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/device/recorder.ts  (buildFingerprint at record time)
 * - packages/healing/src/index.test.ts  (unit tests)
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * FingerprintInput  — { node, allNodes, screenWidth, screenHeight }
 * computeFingerprint(input) → Fingerprint
 *   Builds the full Fingerprint struct for a node at record time.
 *   The fingerprint is attached to the Selector and used by heal() to
 *   find the same element on a future screen dump.
 *
 * CALLER → CALLEE MAP
 * --------------------
 * recorder.ts → computeFingerprint → computeScreenSignature (screen.ts)
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. This function is called at RECORD time, not replay time. It runs
 *    synchronously so it does not block the recorder's event loop.
 * 2. semanticText is the same text that heal() will embed at heal time.
 *    It must include the most discriminative attributes: resourceId,
 *    identifier, text, contentDesc, className — in that order, joined by "|".
 *    Empty tokens are skipped so the embedding is not polluted by empty strings.
 * 3. boundsRatio normalizes to [0..1] using screen dimensions at record time.
 *    The heal() function compares against the element's live bounds normalized
 *    at run time using the SAME screen size (stored in TreeSnapshot.screenSize).
 * 4. Neighbor search uses center-point proximity, not bounding-box proximity.
 *    Only nodes with at least one non-empty text label are considered neighbors.
 *
 * ISSUES FACED
 * ------------
 * None
 *
 * =============================================================================
 */
import { computeScreenSignature } from "./screen.js";
// ── computeFingerprint ────────────────────────────────────────────────────────
/**
 * Build a Fingerprint for an A11yNode at record time.
 *
 * Called by the recorder immediately after resolving which node was tapped.
 * The returned Fingerprint is stored in the IR Selector and used by heal()
 * when Tier-0 candidate matching fails.
 */
export function computeFingerprint(input) {
    const { node, allNodes, screenWidth, screenHeight } = input;
    // semanticText: pipe-separated non-empty attribute values
    const semanticParts = [
        node.resourceId,
        node.identifier,
        node.text,
        node.contentDesc,
        node.className,
    ].filter((v) => typeof v === "string" && v.trim().length > 0);
    const semanticText = semanticParts.join("|");
    // boundsRatio: [x, y, w, h] normalized 0..1
    const sw = screenWidth > 0 ? screenWidth : 1;
    const sh = screenHeight > 0 ? screenHeight : 1;
    const boundsRatio = [
        node.bounds.left / sw,
        node.bounds.top / sh,
        (node.bounds.right - node.bounds.left) / sw,
        (node.bounds.bottom - node.bounds.top) / sh,
    ];
    // neighbors: nearest labeled node in each compass direction
    const cx = (node.bounds.left + node.bounds.right) / 2;
    const cy = (node.bounds.top + node.bounds.bottom) / 2;
    let above;
    let below;
    let left;
    let right;
    let minAbove = Infinity;
    let minBelow = Infinity;
    let minLeft = Infinity;
    let minRight = Infinity;
    for (const n of allNodes) {
        if (n === node)
            continue;
        const label = (n.text || n.contentDesc || n.resourceId || "").trim();
        if (!label)
            continue;
        const nx = (n.bounds.left + n.bounds.right) / 2;
        const ny = (n.bounds.top + n.bounds.bottom) / 2;
        const dy = ny - cy;
        const dx = nx - cx;
        if (dy < 0 && -dy < minAbove) {
            minAbove = -dy;
            above = label;
        }
        if (dy > 0 && dy < minBelow) {
            minBelow = dy;
            below = label;
        }
        if (dx < 0 && -dx < minLeft) {
            minLeft = -dx;
            left = label;
        }
        if (dx > 0 && dx < minRight) {
            minRight = dx;
            right = label;
        }
    }
    // screenSignature: sorted pipe-separated visible texts
    const screenSignature = computeScreenSignature(allNodes);
    return {
        semanticText,
        boundsRatio,
        neighbors: {
            ...(above !== undefined ? { above } : {}),
            ...(below !== undefined ? { below } : {}),
            ...(left !== undefined ? { left } : {}),
            ...(right !== undefined ? { right } : {}),
        },
        screenSignature,
    };
}
//# sourceMappingURL=fingerprint.js.map